import { useState } from "react";
import type { FormEvent } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import { useCatalogoDeTickets } from "../../nucleo/tickets";
import {
  ROTULO_DO_TIPO_DE_CAMPO,
  TIPOS_DE_CAMPO,
  textoDoValor,
  useAlterarCampo,
  useCriarCampo,
  useDefinicoesDeCampo,
  type DefinicaoDeCampo,
  type TipoDeCampo,
} from "../../nucleo/tickets-avancado";
import { CaixaDeMarcar, CampoDeEscolha } from "../automacao/apoio";
import { Aviso, Botao, Campo, CampoLongo, Cartao, Chip } from "../ui";
import { EntradaDeCampo } from "./EntradaDeCampo";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";

/** Converte o texto de uma opção de condição no valor do tipo do campo de que ela depende. */
function valorDaCondicao(campo: DefinicaoDeCampo, texto: string): unknown {
  if (campo.tipo === "sim_nao") return texto === "sim";
  if (campo.tipo === "numero") return Number(texto);
  return texto;
}

function descricaoDaCondicao(campo: DefinicaoDeCampo, todos: readonly DefinicaoDeCampo[]): string | null {
  if (!campo.condicao) return null;
  const outro = todos.find((item) => item.id === campo.condicao?.campo_id);
  return `Aparece quando “${outro?.rotulo ?? "outro campo"}” for ${textoDoValor(campo.condicao.valor)}`;
}

export function PainelDeCampos() {
  const campos = useDefinicoesDeCampo();
  const catalogo = useCatalogoDeTickets();
  const criar = useCriarCampo();
  const voltarParaHome = useVoltarParaHome();
  const alterar = useAlterarCampo();
  const [rotulo, setRotulo] = useState("");
  const [tipo, setTipo] = useState<TipoDeCampo>("texto");
  const [opcoes, setOpcoes] = useState("");
  const [obrigatorio, setObrigatorio] = useState(false);
  const [visivelAoCliente, setVisivelAoCliente] = useState(false);
  const [categoria, setCategoria] = useState("");
  const [servico, setServico] = useState("");
  const [dependeDe, setDependeDe] = useState("");
  const [valorDaDependencia, setValorDaDependencia] = useState<unknown>(null);

  const comOpcoes = tipo === "lista" || tipo === "multipla";
  const todos = campos.data ?? [];
  const campoDaCondicao = todos.find((item) => item.id === dependeDe);
  const servicosDaCategoria = (catalogo.servicos.data ?? []).filter(
    (item) => item.categoria_id === catalogo.categorias.data?.find((c) => c.nome === categoria)?.id,
  );
  const listaDeOpcoes = opcoes
    .split("\n")
    .map((linha) => linha.trim())
    .filter(Boolean);
  const faltaCondicao = Boolean(dependeDe) && (valorDaDependencia === null || valorDaDependencia === "");
  const invalido = rotulo.trim().length < 2 || (comOpcoes && listaDeOpcoes.length === 0) || faltaCondicao;

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    if (invalido) return;
    criar.mutate(
      {
        rotulo: rotulo.trim(),
        tipo,
        opcoes: comOpcoes ? listaDeOpcoes : [],
        obrigatorio,
        visivel_ao_cliente: visivelAoCliente,
        categoria: categoria || null,
        servico: servico || null,
        condicao: dependeDe ? { campo_id: dependeDe, valor: valorDaDependencia } : null,
      },
      {
        onSuccess: () => {
          setRotulo("");
          setOpcoes("");
          setObrigatorio(false);
          setVisivelAoCliente(false);
          setCategoria("");
          setServico("");
          setDependeDe("");
          setValorDaDependencia(null);
          voltarParaHome("Campo personalizado criado.");
        },
      },
    );
  }

  const falha = criar.error ?? alterar.error ?? campos.error;

  return (
    <div>
      <Cartao titulo="Novo campo personalizado" className="mb-4">
        <form onSubmit={submeter} data-testid="formulario-de-campo" noValidate>
          <Campo rotulo="Nome do campo" value={rotulo} onChange={(e) => setRotulo(e.target.value)} maxLength={120} />
          <CampoDeEscolha
            rotulo="Tipo"
            valor={tipo}
            aoMudar={(valor) => setTipo(valor as TipoDeCampo)}
            opcoes={TIPOS_DE_CAMPO.map((t) => ({ valor: t, rotulo: ROTULO_DO_TIPO_DE_CAMPO[t] }))}
          />
          {comOpcoes ? (
            <CampoLongo rotulo="Opções" ajuda="Uma por linha." value={opcoes} onChange={(e) => setOpcoes(e.target.value)} />
          ) : null}
          <CaixaDeMarcar rotulo="Obrigatório" marcada={obrigatorio} aoMudar={setObrigatorio} ajuda="Sem resposta, o chamado não é aberto." />
          <CaixaDeMarcar
            rotulo="Visível ao cliente"
            marcada={visivelAoCliente}
            aoMudar={setVisivelAoCliente}
            ajuda="Desmarcado, é um campo interno: o cliente nunca o vê."
          />
          <CampoDeEscolha
            rotulo="Vale para a categoria"
            valor={categoria}
            aoMudar={(valor) => {
              setCategoria(valor);
              setServico("");
            }}
            vazio="Todas as categorias"
            opcoes={(catalogo.categorias.data ?? []).map((c) => ({ valor: c.nome, rotulo: c.nome }))}
          />
          {categoria ? (
            <CampoDeEscolha
              rotulo="Vale para o serviço"
              valor={servico}
              aoMudar={setServico}
              vazio="Todos os serviços"
              opcoes={servicosDaCategoria.map((s) => ({ valor: s.nome, rotulo: s.nome }))}
            />
          ) : null}
          <CampoDeEscolha
            rotulo="Mostrar somente se outro campo tiver um valor"
            valor={dependeDe}
            aoMudar={(valor) => {
              setDependeDe(valor);
              setValorDaDependencia(null);
            }}
            vazio="Sempre mostrar"
            opcoes={todos
              .filter((c) => c.ativo && (!visivelAoCliente || c.visivel_ao_cliente))
              .map((c) => ({ valor: c.id, rotulo: c.rotulo }))}
          />
          {campoDaCondicao ? (
            campoDaCondicao.tipo === "texto" || campoDaCondicao.tipo === "data" || campoDaCondicao.tipo === "numero" ? (
              <Campo
                rotulo="O valor que faz este campo aparecer"
                type={campoDaCondicao.tipo === "texto" ? "text" : campoDaCondicao.tipo === "data" ? "date" : "number"}
                value={typeof valorDaDependencia === "string" || typeof valorDaDependencia === "number" ? String(valorDaDependencia) : ""}
                onChange={(e) => setValorDaDependencia(e.target.value === "" ? null : valorDaCondicao(campoDaCondicao, e.target.value))}
              />
            ) : (
              <EntradaDeCampo
                campo={{
                  ...campoDaCondicao,
                  rotulo: "O valor que faz este campo aparecer",
                  obrigatorio: false,
                  // Numa condição de múltipla escolha vale UMA opção: a lista comporta isso.
                  tipo: campoDaCondicao.tipo === "multipla" ? "lista" : campoDaCondicao.tipo,
                }}
                valor={valorDaDependencia}
                aoMudar={setValorDaDependencia}
              />
            )
          ) : null}
          {falha ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(falha)}</Aviso> : null}
          <Botao type="submit" disabled={invalido || criar.isPending}>{criar.isPending ? "Criando…" : "Criar campo"}</Botao>
        </form>
      </Cartao>

      <section aria-labelledby="titulo-campos">
        <h2 className="titulo-secao" id="titulo-campos">Campos cadastrados</h2>
        {campos.isPending ? <p role="status">Carregando campos…</p> : null}
        {campos.data && campos.data.length === 0 ? <Aviso tom="marca">Nenhum campo personalizado ainda.</Aviso> : null}
        <div className="lista-de-itens" data-testid="lista-de-campos">
          {todos.map((campo) => (
            <Cartao
              key={campo.id}
              titulo={campo.rotulo}
              data-testid={`campo-${campo.rotulo}`}
              acessorio={<Chip estado={campo.ativo ? "ok" : "neutro"}>{campo.ativo ? "Ativo" : "Desativado"}</Chip>}
            >
              <p>
                {ROTULO_DO_TIPO_DE_CAMPO[campo.tipo]}
                {campo.opcoes.length ? ` (${campo.opcoes.join(", ")})` : ""} · {campo.obrigatorio ? "obrigatório" : "opcional"} ·{" "}
                {campo.visivel_ao_cliente ? "visível ao cliente" : "só a equipe vê"}
              </p>
              <p className="nota-rodape">
                {campo.categoria ? `Categoria ${campo.categoria}${campo.servico ? ` · serviço ${campo.servico}` : ""}` : "Vale para todos os chamados"}
                {descricaoDaCondicao(campo, todos) ? ` · ${descricaoDaCondicao(campo, todos)}` : ""}
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <Botao
                  variante="fantasma"
                  tamanho="pequeno"
                  aria-label={`${campo.ativo ? "Desativar" : "Reativar"} o campo ${campo.rotulo}`}
                  onClick={() => alterar.mutate({ id: campo.id, mudancas: { ativo: !campo.ativo } })}
                >
                  {campo.ativo ? "Desativar" : "Reativar"}
                </Botao>
                <Botao
                  variante="fantasma"
                  tamanho="pequeno"
                  aria-label={`Tornar o campo ${campo.rotulo} ${campo.obrigatorio ? "opcional" : "obrigatório"}`}
                  onClick={() => alterar.mutate({ id: campo.id, mudancas: { obrigatorio: !campo.obrigatorio } })}
                >
                  {campo.obrigatorio ? "Tornar opcional" : "Tornar obrigatório"}
                </Botao>
              </div>
            </Cartao>
          ))}
        </div>
      </section>
    </div>
  );
}
